import { getPlayerColour, listNations, PLAYER_COLOURS } from '@brig/nations';
import type { RuleOptions } from '@brig/api-types';
import { DEFAULT_GAME_SIZE, otherSide, setupSize, type GameSizeId, type Side } from '@brig/rules-engine';
import { use, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBoardCamera, useCellPx } from '../../board/boardCamera.ts';
import { BoardView } from '../../board/BoardView.tsx';
import type { MapStyle } from '../../board/chartStyle.ts';
import { centerOnCell } from '../../board/focus.ts';
import { SizePicker } from '../SizePicker.tsx';
import { ShipSprite } from '../../ships/ShipSprite.tsx';
import { headingAfterMove, headingTowardEnemy, type Heading } from '../../ships/sides.ts';
import { TurnOverlays } from '../TurnControls.tsx';
import { shipsToMove, squadronCandidates } from '../turn.ts';
import { BoardCaptions } from '../BoardCaptions.tsx';
import { BottomSheet } from '../BottomSheet.tsx';
import { attackersOf } from '../fire.ts';
import { FirePanel } from '../FirePanel.tsx';
import { FleetRoster } from '../FleetRoster.tsx';
import { dueHints, HINT_TOPIC, nextHint } from '../../learn/firstHints.ts';
import { HintCard } from '../../learn/HintCard.tsx';
import { HintMemoryContext, useLocalHintMemory } from '../../learn/hintMemory.ts';
import { HowToPlaySheet } from '../../learn/HowToPlay.tsx';
import type { Topic } from '../../learn/topics.ts';
import * as gameStyles from '../Game.css.ts';
import {
  CellPicker,
  HudStatus,
  HudTools,
  Toasts,
  TooltipLayer,
  TurnCorner,
  UnitTipContent,
  type Panel,
  type PickSquadron,
  type Toast,
} from '../Hud.tsx';
import * as hudStyles from '../Hud.css.ts';
import { GameMenu, HelpMenu, Section, SettingsMenu } from '../Menus.tsx';
import { unitLabel } from '../names.ts';
import { uiPhase } from '../phase.ts';
import { reachOf, whyNotThere } from '../reach.ts';
import { ShipInspector } from '../ShipInspector.tsx';
import { INSPECTOR_WIDTH } from '../ShipInspector.css.ts';
import { TurnLog } from '../TurnLog.tsx';
import { useBoardFeedback } from '../useBoardFeedback.ts';
import { markSeen, seen, useCompact } from '../useCompact.ts';
import { useHotkeys } from '../useHotkeys.ts';
import { useKeepClear } from '../useKeepClear.ts';
import { ReplayBar } from '../ViewControls.tsx';
import { errorText, whyText } from '../why.ts';
import { useDocumentTitle } from '../../useDocumentTitle.ts';
import { GameExit } from '../exit.ts';
import { SandboxNations } from './nationsContext.ts';
import { randomSeed, type Nations } from '../history.ts';
import * as styles from './Sandbox.css.ts';
import { useGameScreen, type GameSource } from './useGameScreen.ts';

type BooleanOption = 'oarsAndSteamUseBest' | 'passThroughShips' | 'landBlocksFire' | 'diagonalSqueeze';
const BOOLEAN_OPTIONS: readonly BooleanOption[] = [
  'oarsAndSteamUseBest',
  'passThroughShips',
  'landBlocksFire',
  'diagonalSqueeze',
];

/** How long a "why not there" note stays over the board, ms. */
const NOTE_MS = 3500;

interface Pick {
  readonly ids: readonly string[];
  readonly at: { readonly x: number; readonly y: number };
  /** A tap's meaning changes with the phase (U-MUST-01): a picker opened before it is gone. */
  readonly phase: string;
}

/**
 * The hot-seat game screen: both sides at one screen, the game kept by the server (or, in
 * tests, by the rules engine in the browser). The board fills the screen; the HUD floats over
 * it (docs/ux.md §2, §13) and the dev tools sit in the menu's Developer section.
 */
export function GameScreen({ source, title = 'Sandbox' }: { source: GameSource; title?: string }) {
  useDocumentTitle(title);
  const { t } = useTranslation();
  const sb = useGameScreen(source);
  const { history } = sb;
  const replay = sb.snapshot.context.replay;
  // During a replay the board shows the opponent's turn step by step (U-LOG-05).
  const state = replay ? (history.states[replay.at] ?? sb.state) : sb.state;
  const projection = sb.snapshot.context.view === 'chart' ? 'flat' : 'iso';
  // One camera per page: the board, its minimap and the readout below share it.
  const [camera] = useState(createBoardCamera);
  const cellPx = useCellPx(camera);
  const [mapStyle, setMapStyle] = useState<MapStyle>('chart');
  // Compact screens (§13): sheets from the bottom, the minimap folded away at first.
  const compact = useCompact();
  const [panel, setPanel] = useState<Panel | null>(null);
  // Phones: the inspector folded to a bar, or opened into a sheet (U-CTX-01).
  const [inspectorOpen, setInspectorOpen] = useState(false);
  // A tap or click only selects a unit; a long press or right click shows its card until it
  // is closed (U-CTX-01).
  const [cardFor, setCardFor] = useState<string | null>(null);
  const [minimap, setMinimap] = useState<boolean | null>(null);
  const showMinimap = minimap ?? !compact;
  const [pick, setPick] = useState<Pick | null>(null);
  const [notes, setNotes] = useState<readonly Toast[]>([]);
  const [chartHint, setChartHint] = useState(() => !seen('chartHint'));
  // The page "How to play" opens at: a hint's "More…" opens its own.
  const [learnTopic, setLearnTopic] = useState<Topic>('goal');
  const profileHints = use(HintMemoryContext);
  const localHints = useLocalHintMemory();
  const hintMemory = profileHints ?? localHints;
  const feedback = useBoardFeedback(sb.snapshot, camera, projection);
  const pointOut = feedback.pointOut;
  const myFleet = useCallback(() => {
    pointOut(sb.state.ships.filter((s) => s.side === sb.state.turn.side && !s.sunk).map((s) => s.cell));
  }, [pointOut, sb.state]);

  /** U-KEY-01, Esc: the topmost layer of the HUD closes first, then the turn's own. */
  const closeTop = (): boolean => {
    if (pick?.phase === phase) setPick(null);
    else if (panel) setPanel(null);
    else if (compact && inspectorOpen && cardFor !== null && cardFor === sb.selected) setInspectorOpen(false);
    else if (cardFor !== null && cardFor === sb.selected) setCardFor(null);
    else return false;
    return true;
  };
  useHotkeys(sb.snapshot, sb.send, {
    onMyFleet: myFleet,
    onToggleLog: () => {
      setPanel((v) => (v === 'log' ? null : 'log'));
    },
    onKeys: () => {
      setPanel((v) => (v === 'help' ? null : 'help'));
    },
    onEscape: closeTop,
    onCard: () => {
      setCardFor(sb.selected);
      if (compact) setInspectorOpen(true);
    },
  });
  const colours = useMemo(
    () => ({
      red: getPlayerColour(history.colours.red).hex,
      blue: getPlayerColour(history.colours.blue).hex,
    }),
    [history.colours],
  );

  const facing = useMemo(() => {
    const out = new Map<string, Heading>();
    const start = history.states[0]?.ships ?? [];
    const toward = headingTowardEnemy(start);
    for (const s of start) out.set(s.id, toward[s.side]);
    for (const e of history.events.flat()) {
      if (e.type === 'shipMoved') out.set(e.shipId, headingAfterMove(e.from, e.to, out.get(e.shipId)));
    }
    return out;
  }, [history.states, history.events]);
  const movable = useMemo(() => new Set(shipsToMove(state)), [state]);

  const phase = uiPhase(sb.snapshot);
  const playing = phase === 'muster' || phase === 'battle';

  const moveCells = sb.fire ? noCells : sb.hints.moveCells;
  // U-MOVE-02: the selected ship's rays, with what blocks them.
  const reach = useMemo(
    () => (sb.selected ? reachOf(state, sb.selected, moveCells) : null),
    [state, sb.selected, moveCells],
  );
  const showChartHint =
    compact && chartHint && playing && sb.snapshot.context.view === 'perspective' && !panel;
  const dismissChartHint = (toChart: boolean) => {
    if (toChart) sb.send({ type: 'TOGGLE_VIEW' });
    markSeen('chartHint');
    setChartHint(false);
  };
  const nameOf = (id: string) => unitLabel(t, state, history.nations, id);
  // U-FIRE-01: who could fire at the enemy under the pointer, worked out once per position.
  const attackers = useMemo(() => {
    const units = [...state.ships, ...state.forts].map((u) => u.id);
    const by = new Map(units.map((id) => [id, attackersOf(state, id)]));
    return (id: string): readonly string[] => by.get(id) ?? [];
  }, [state]);
  const focus = sb.selected ? (state.ships.find((x) => x.id === sb.selected)?.cell ?? null) : null;

  const note = (text: string) => {
    const key = `${Date.now()}-${text}`;
    setNotes((list) => [...list.slice(-1), { key, text }]);
    setTimeout(() => {
      setNotes((list) => list.filter((n) => n.key !== key));
    }, NOTE_MS);
  };

  /** U-MOVE-02, U-WHY-04: a tap on a cell the wind allows but something blocks says why. */
  const onCell = (cell: string) => {
    if (sb.selected && reach?.blocked.has(cell)) {
      const reason = whyNotThere(state, sb.selected, cell);
      if (reason) note(t('game.reach.blocked', { cell, why: whyText(t, state, reason) }));
    }
    sb.onCell(cell);
  };

  /**
   * U-SEL-02: a cell with two ships opens the picker, unless only one of them is what the tap
   * can mean now: the enemy the selected shooter can hit, or the one ship that may still join
   * the squadron. (A mouse click on one ship's own artwork never gets here: BoardView.)
   */
  const onPickShared = (ids: readonly string[], at: Pick['at']) => {
    const direct = ids.filter((id) => {
      const ship = state.ships.find((s) => s.id === id);
      if (!ship) return false;
      if (ship.side !== state.turn.side)
        return sb.hints.targets.has(id) || (!sb.selected && sb.fire !== null);
      return phase === 'muster' && squadronCandidates(state).includes(id) && !sb.hints.targets.size;
    });
    const only = direct.length === 1 ? direct[0] : undefined;
    if (only) sb.onUnit(only);
    else setPick({ ids, at, phase });
  };

  // While the squadron is chosen, the picker toggles ships and stays open for the other one.
  const candidates = useMemo(() => new Set(squadronCandidates(state)), [state]);
  const pickSquadron: PickSquadron | undefined =
    phase === 'muster' && !sb.fire ? { chosen: sb.squadron, candidates } : undefined;
  const onPicked = (id: string) => {
    if (!pickSquadron?.candidates.has(id)) setPick(null);
    sb.onUnit(id);
  };

  const toasts: Toast[] = [...notes];
  if (history.error) {
    toasts.unshift({
      key: 'error',
      text: errorText(t, sb.state, history.error),
      tone: 'bad',
      onDismiss: () => {
        sb.send({ type: 'DISMISS_ERROR' });
      },
    });
  }
  const togglePanel = (p: Panel) => () => {
    if (p === 'learn') setLearnTopic('goal');
    setPanel((v) => (v === p ? null : p));
  };
  const closePanel = () => {
    setPanel(null);
  };
  const centreOnSelected = () => {
    const cell = focus ?? state.forts.find((f) => f.id === sb.selected)?.cell ?? null;
    if (cell) centerOnCell(camera, projection, cell);
  };
  // U-CTX-01: one card for the selected unit, only after a long press or right click on it,
  // and not while aiming. Phones also hide it under a sheet.
  const inspecting =
    sb.selected !== null &&
    cardFor === sb.selected &&
    (playing || phase === 'over') &&
    sb.fire === null &&
    !(compact && panel !== null);
  const portCells = sb.fire || !sb.snapshot.context.portMove ? noCells : sb.hints.portCells;
  const choosing = moveCells.size + portCells.size > 0;
  const inspectorExpanded = compact ? inspectorOpen : true;
  // The ship and where it may sail stay clear of the card (U-CTX-01), as it opens or folds too.
  const clearKey = inspecting
    ? `${sb.selected ?? ''}|${String(choosing)}|${String(inspectorExpanded)}`
    : null;
  useKeepClear(camera, projection, clearKey, () => [...(focus ? [focus] : []), ...moveCells, ...portCells]);
  const closePick = useCallback(() => {
    setPick(null);
  }, []);

  // U-LEARN-06/07: one first-time hint at a calm moment of the player's own turn: no animation,
  // no replay, no panel, dialog, picker or note open.
  const remote = sb.snapshot.context.remote;
  const mySide = remote ? otherSide(remote) : sb.state.turn.side;
  const calm =
    hintMemory.on &&
    !replay &&
    panel === null &&
    sb.fire === null &&
    pick === null &&
    toasts.length === 0 &&
    !showChartHint &&
    (sb.snapshot.matches({ muster: 'choosing' }) || sb.snapshot.matches({ battle: 'playing' }));
  const hintPhase = phase === 'muster' ? 'muster' : 'battle';
  const hint = useMemo(
    () => (calm ? nextHint(dueHints(sb.state, sb.legal, mySide, hintPhase), hintMemory.seen) : null),
    [calm, sb.state, sb.legal, mySide, hintPhase, hintMemory.seen],
  );

  return (
    <SandboxNations.Provider value={history.nations}>
      <GameExit value={sb.exitTo}>
        <div
          className={hudStyles.screen}
          // The board's zoom buttons and minimap step aside for the opened desktop card.
          style={
            inspecting && !compact && inspectorExpanded
              ? { ['--board-right-inset' as string]: `${INSPECTOR_WIDTH + 8}px` }
              : undefined
          }
        >
          <ShipSprite />
          <div className={hudStyles.board}>
            <BoardView
              state={state}
              projection={projection}
              focus={focus}
              cursor={sb.snapshot.context.cursor}
              highlight={feedback.highlight}
              salvo={feedback.salvo}
              selected={sb.selected}
              draftGroup={sb.squadron}
              moveCells={moveCells}
              portCells={portCells}
              targets={sb.hints.targets}
              volleyTarget={sb.fire?.target ?? null}
              movable={movable}
              volleyShips={sb.fireShips}
              attackersOf={playing && !replay ? attackers : noAttackers}
              blockedCells={reach?.blocked ?? noCells}
              facing={facing}
              camera={camera}
              mapStyle={mapStyle}
              colours={colours}
              minimap={showMinimap && !replay}
              onToggleMinimap={() => {
                setMinimap(!showMinimap);
              }}
              unitTip={(id) => <UnitTipContent snapshot={sb.snapshot} id={id} />}
              nations={history.nations}
              onCell={replay ? ignore : onCell}
              onUnit={replay ? ignore : sb.onUnit}
              onUnitHold={
                replay
                  ? ignore
                  : (id) => {
                      // U-TOUCH-03, U-CTX-01: a long press or a right click shows everything about
                      // the unit, acting on nothing.
                      sb.setSelected(id);
                      setCardFor(id);
                      if (compact) setInspectorOpen(true);
                    }
              }
              onPickShared={(_cell, ids, at) => {
                if (!replay) onPickShared(ids, at);
              }}
            />
            <BoardCaptions captions={feedback.captions} camera={camera} projection={projection} />
          </div>

          <div className={hudStyles.layer}>
            <div className={hudStyles.top}>
              <HudStatus snapshot={sb.snapshot} panel={panel} onPanel={setPanel} />
              <HudTools snapshot={sb.snapshot} send={sb.send} panel={panel} onPanel={setPanel} />
            </div>
            {feedback.showCell ? (
              <button type="button" className={gameStyles.showChip} onClick={feedback.show}>
                {t('game.view.show')}
              </button>
            ) : null}
            <ReplayBar snapshot={sb.snapshot} send={sb.send} />
            <Toasts toasts={toasts} />
            {hint ? (
              <HintCard
                hint={hint}
                ruleset={sb.state.ruleset}
                onMore={() => {
                  hintMemory.markSeen(hint);
                  setLearnTopic(HINT_TOPIC[hint]);
                  setPanel('learn');
                }}
                onDismiss={() => {
                  hintMemory.markSeen(hint);
                }}
              />
            ) : null}
            {inspecting && sb.selected ? (
              <ShipInspector
                snapshot={sb.snapshot}
                send={sb.send}
                id={sb.selected}
                hints={sb.hints}
                expanded={inspectorExpanded}
                onExpanded={compact ? setInspectorOpen : undefined}
                onClose={() => {
                  setCardFor(null);
                }}
                onApply={sb.apply}
                onCentre={centreOnSelected}
              />
            ) : null}
            {!(compact && panel) ? <TurnCorner snapshot={sb.snapshot} send={sb.send} /> : null}
            <FirePanel snapshot={sb.snapshot} send={sb.send} nameOf={nameOf} />
            <TurnOverlays snapshot={sb.snapshot} send={sb.send} nameOf={nameOf} />
            {/* U-SEL-02: a small popover by the cell on every screen; its rows are finger-sized. */}
            {pick?.phase === phase ? (
              <CellPicker
                state={state}
                nations={history.nations}
                ids={pick.ids}
                at={pick.at}
                selected={sb.selected}
                squadron={pickSquadron}
                onPick={onPicked}
                onClose={closePick}
              />
            ) : null}
          </div>

          {panel === 'fleet' ? (
            <BottomSheet
              title={t('game.roster.title')}
              icon="fleet"
              onClose={closePanel}
              testId="sheet-fleet"
              placement="left"
            >
              <FleetRoster
                snapshot={sb.snapshot}
                send={sb.send}
                onPicked={compact ? closePanel : ignore}
                onCentre={(cell) => {
                  if (compact) closePanel();
                  centerOnCell(camera, projection, cell);
                }}
              />
            </BottomSheet>
          ) : null}
          {panel === 'log' ? (
            <BottomSheet
              title={t('game.log.title')}
              icon="log"
              onClose={closePanel}
              testId="sheet-log"
              actions={
                <button type="button" className={gameStyles.button} onClick={sb.exportLog}>
                  {t('game.log.export')}
                </button>
              }
            >
              <TurnLog
                history={history}
                onPick={(entry) => {
                  if (compact) closePanel();
                  pointOut(entry.cells);
                }}
              />
            </BottomSheet>
          ) : null}
          {panel === 'menu' ? (
            <BottomSheet title={t('game.menu.title')} icon="menu" onClose={closePanel} testId="sheet-menu">
              <button type="button" className={gameStyles.primary} onClick={togglePanel('newGame')}>
                {t('game.menu.newGame')}
              </button>
              <GameMenu snapshot={sb.snapshot} onApply={sb.apply} />
              <button type="button" className={gameStyles.button} onClick={togglePanel('learn')}>
                {t('learn.open')}
              </button>
              {/* Phones keep only fleet and log in the top bar: settings and help live here. */}
              {compact ? (
                <>
                  <SettingsMenu
                    snapshot={sb.snapshot}
                    send={sb.send}
                    mapStyle={mapStyle}
                    onMapStyle={setMapStyle}
                    hints={hintMemory}
                  />
                  <button type="button" className={gameStyles.button} onClick={togglePanel('help')}>
                    {t('game.hud.help')}
                  </button>
                </>
              ) : null}
              {sb.devTools ? (
                <Section title={t('game.menu.developer')}>
                  <span className={styles.small} data-testid="cell-size" title="Cell size at this zoom">
                    DEV ONLY · cell {Math.round(cellPx)} px
                  </span>
                  <DevPanel sb={sb} />
                </Section>
              ) : null}
            </BottomSheet>
          ) : null}
          {panel === 'newGame' ? (
            <BottomSheet
              title={t('game.menu.newGame')}
              icon="plus"
              onClose={closePanel}
              testId="sheet-new-game"
            >
              <NewGame sb={sb} onStarted={closePanel} />
            </BottomSheet>
          ) : null}
          {panel === 'settings' ? (
            <BottomSheet
              title={t('game.menu.settings')}
              icon="settings"
              onClose={closePanel}
              testId="sheet-settings"
            >
              <SettingsMenu
                snapshot={sb.snapshot}
                send={sb.send}
                mapStyle={mapStyle}
                onMapStyle={setMapStyle}
                hints={hintMemory}
              />
            </BottomSheet>
          ) : null}
          {panel === 'help' ? (
            <BottomSheet title={t('game.help.title')} icon="help" onClose={closePanel} testId="sheet-help">
              <button type="button" className={gameStyles.primary} onClick={togglePanel('learn')}>
                {t('learn.open')}
              </button>
              <HelpMenu />
            </BottomSheet>
          ) : null}
          {panel === 'learn' ? (
            <BottomSheet title={t('learn.title')} icon="help" onClose={closePanel} testId="sheet-learn">
              <HowToPlaySheet key={learnTopic} ruleset={sb.state.ruleset} start={learnTopic} />
            </BottomSheet>
          ) : null}
          {showChartHint ? (
            <BottomSheet
              title={t('game.chartHint.title')}
              onClose={() => {
                dismissChartHint(false);
              }}
              testId="sheet-chart-hint"
            >
              <p className={styles.small}>{t('game.chartHint.body')}</p>
              <div className={styles.row}>
                <button
                  type="button"
                  className={gameStyles.primary}
                  onClick={() => {
                    dismissChartHint(true);
                  }}
                >
                  {t('game.chartHint.switch')}
                </button>
                <button
                  type="button"
                  className={gameStyles.button}
                  onClick={() => {
                    dismissChartHint(false);
                  }}
                >
                  {t('game.chartHint.keep')}
                </button>
              </div>
            </BottomSheet>
          ) : null}
          <TooltipLayer />
        </div>
      </GameExit>
    </SandboxNations.Provider>
  );
}

const ignore = () => undefined;
const noCells: ReadonlySet<string> = new Set();
const noAttackers = (): readonly string[] => [];

type Screen = ReturnType<typeof useGameScreen>;

const SIDES: readonly Side[] = ['red', 'blue'];

/**
 * Each side's nation (for the next game: the players are named after it) and colour (at once:
 * it only marks their ships, ports and Royal Port on the board). The two sides never share
 * either.
 */
function PlayerPicks({
  nations,
  onNations,
  nationsFixed = false,
  colours,
  onColours,
}: {
  nations: Nations;
  onNations: (n: Nations) => void;
  /** The game under way: its nations are shown, not changed (its colours may be). */
  nationsFixed?: boolean;
  colours: Readonly<Record<Side, string>>;
  onColours: (c: Readonly<Record<Side, string>>) => void;
}) {
  return (
    <>
      {SIDES.map((side) => {
        const other = side === 'red' ? 'blue' : 'red';
        return (
          <div key={side} className={styles.row} role="group" aria-label={`${side} player`}>
            <select
              aria-label={`${side} nation`}
              disabled={nationsFixed}
              value={nations[side]}
              onChange={(e) => {
                onNations({ ...nations, [side]: e.target.value });
              }}
            >
              {listNations().map((n) => (
                <option key={n.id} value={n.id} disabled={n.id === nations[other]}>
                  {n.names.en}
                </option>
              ))}
            </select>
            {PLAYER_COLOURS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={styles.colourChoice}
                style={{ background: c.hex }}
                title={c.names.en}
                aria-label={`${side}: ${c.names.en}`}
                aria-pressed={colours[side] === c.id}
                disabled={colours[other] === c.id}
                onClick={() => {
                  onColours({ ...colours, [side]: c.id });
                }}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

/**
 * The next game's setup, a sheet of its own (from the menu): its size (map, forts and fleet,
 * R-SIZE-*), each side's nation and colour, the seed and the rule options. Nothing changes
 * until "Start".
 */
function NewGame({ sb, onStarted }: { sb: Screen; onStarted: () => void }) {
  const { t } = useTranslation();
  const { state, history } = sb;
  const [size, setSize] = useState<GameSizeId>(() => {
    const playing = setupSize(history.setup) ?? DEFAULT_GAME_SIZE;
    return sb.sizes.includes(playing) ? playing : (sb.sizes[0] ?? playing);
  });
  const [nations, setNations] = useState<Nations>(history.nations);
  const [colours, setColours] = useState<Readonly<Record<Side, string>>>(history.colours);
  const [seed, setSeed] = useState(String(randomSeed()));
  const [options, setOptions] = useState<RuleOptions>({});
  const start = () => {
    sb.newGame(sb.devTools ? Number(seed) || 0 : null, options, nations, colours, size);
    onStarted();
  };
  return (
    <section className={styles.panel} aria-label="New game" data-testid="new-game">
      <SizePicker value={size} onChange={setSize} sizes={sb.sizes} />
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Players</legend>
        <PlayerPicks nations={nations} onNations={setNations} colours={colours} onColours={setColours} />
      </fieldset>
      {sb.devTools ? (
        <DevOptions state={state} seed={seed} onSeed={setSeed} options={options} onOptions={setOptions} />
      ) : null}
      <button type="button" className={gameStyles.primary} onClick={start}>
        {t('game.size.start', { size: t(`game.size.${size}.name`) })}
      </button>
    </section>
  );
}

/** Dev tools of a new game: a fixed seed and the rule options. */
function DevOptions({
  state,
  seed,
  onSeed: setSeed,
  options,
  onOptions: setOptions,
}: {
  state: Screen['state'];
  seed: string;
  onSeed: (seed: string) => void;
  options: RuleOptions;
  onOptions: (options: RuleOptions) => void;
}) {
  return (
    <>
      <div className={styles.row}>
        <label className={styles.row}>
          Seed
          <input
            className={styles.input}
            aria-label="Seed"
            inputMode="numeric"
            value={seed}
            onChange={(e) => {
              setSeed(e.target.value.replace(/\D/g, ''));
            }}
          />
        </label>
        <button
          type="button"
          className={styles.button}
          onClick={() => {
            setSeed(String(randomSeed()));
          }}
        >
          Random seed
        </button>
      </div>
      <details>
        <summary>Rule options</summary>
        {BOOLEAN_OPTIONS.map((key) => (
          <label key={key} className={styles.row}>
            <input
              type="checkbox"
              checked={options[key] ?? state.ruleset[key]}
              onChange={(e) => {
                setOptions({ ...options, [key]: e.target.checked });
              }}
            />
            {key}
          </label>
        ))}
        <label className={styles.row}>
          hitChance
          <select
            value={String(options.hitChance ?? 1 / 4)}
            onChange={(e) => {
              setOptions({ ...options, hitChance: Number(e.target.value) });
            }}
          >
            <option value={String(1 / 6)}>1/6</option>
            <option value={String(1 / 4)}>1/4 (rules)</option>
            <option value={String(1 / 3)}>1/3</option>
            <option value="0.5">1/2</option>
            <option value="1">always hit</option>
          </select>
        </label>
      </details>
    </>
  );
}

/** The menu's Developer section: the game's seed and length, its log, and rewind. */
function DevPanel({ sb }: { sb: Screen }) {
  const { t } = useTranslation();
  const { history } = sb;
  const playing = setupSize(history.setup);
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <section className={styles.panel} aria-label="Game">
      <div className={styles.small}>
        Seed {history.setup.seed} · {history.actions.length} action(s) ·{' '}
        {playing ? t(`game.size.${playing}.name`) : t('game.size.classic')}
      </div>
      <PlayerPicks
        nations={history.nations}
        onNations={ignore}
        nationsFixed
        colours={history.colours}
        onColours={sb.recolour}
      />
      <div className={styles.row}>
        <button type="button" className={styles.button} onClick={sb.exportLog}>
          Export log
        </button>
        <button
          type="button"
          className={styles.button}
          disabled={history.actions.length === 0}
          title="Dev tool: take back any action, the wind and shots included"
          onClick={sb.rewind}
        >
          Rewind
        </button>
        <button type="button" className={styles.button} onClick={() => fileRef.current?.click()}>
          Import log
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          aria-label="Import log file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void file.text().then(sb.load);
          }}
        />
      </div>
    </section>
  );
}
